// src/components/BarberUpload.jsx

import React, { useState, useRef, useEffect } from 'react';
import { db, storage, auth } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, doc, writeBatch, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const DARK = '#010810';

const FACE_SHAPES = ['All Face Shapes', 'Oval', 'Round', 'Square', 'Diamond', 'Heart', 'Oblong'];
const CATEGORIES = ['Hair', 'Beard', 'Hair + Beard', 'Colour', 'Braids', 'Locs', 'Kids Cut', 'Special'];
const DURATIONS = ['15 min', '30 min', '45 min', '1 hour', '1.5 hours', '2 hours', '2+ hours'];

export default function BarberUpload({ barberData, onClose, editItem = null }) {
  const user = auth.currentUser;
  const isEditMode = !!editItem;

  // Form States
  const [step, setStep] = useState(isEditMode ? 'form' : 'pick');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(editItem?.imageUrl || null);
  
  const [name, setName] = useState(editItem?.name || '');
  const [price, setPrice] = useState(editItem?.price ? String(editItem.price) : '');
  const [category, setCategory] = useState(editItem?.category || 'Hair');
  const [duration, setDuration] = useState(editItem?.duration || '45 min');
  const [description, setDescription] = useState(editItem?.description || '');
  const [faceShapes, setFaceShapes] = useState(editItem?.faceShapes || ['All Face Shapes']);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);

  const fileRef = useRef(null);
  const objectUrlRef = useRef(null); // For memory cleanup

  // Price validation
  const parsedPrice = parseFloat(price.replace(/[^0-9.]/g, ''));
  const priceValid = !isNaN(parsedPrice) && parsedPrice > 0;

  // Safe data fallbacks
  const safeDisplayName = barberData?.displayName || barberData?.barberName || user?.displayName || 'Professional Stylist';
  const safeBusinessName = barberData?.businessName || barberData?.shopName || '';
  const safePhoto = barberData?.photoURL || user?.photoURL || '';
  const safeLocation = barberData?.location || '';
  const safeReferral = barberData?.referralCode || '';

  // ==================== IMAGE COMPRESSION ====================
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;

          const MAX_DIM = 1200;
          if (width > MAX_DIM || height > MAX_DIM) {
            if (width > height) {
              height = Math.round((height * MAX_DIM) / width);
              width = MAX_DIM;
            } else {
              width = Math.round((width * MAX_DIM) / height);
              height = MAX_DIM;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('Compression failed'));
              const compressedFile = new File([blob], file.name.replace(/\.\w+$/, '.jpg'), {
                type: 'image/jpeg',
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.82
          );
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  // ==================== FILE HANDLER ====================
  const handleFileSelect = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!selected.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }

    if (selected.size > 15 * 1024 * 1024) {
      setError('Image too large (max 15MB before compression)');
      return;
    }

    setError('');
    setIsCompressing(true);

    let fileToUpload = selected;

    try {
      if (selected.size > 5 * 1024 * 1024) {
        const loadingToast = toast.loading('Compressing image...');
        fileToUpload = await compressImage(selected);
        toast.dismiss(loadingToast);
      }

      // Cleanup old preview URL
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }

      const newPreviewUrl = URL.createObjectURL(fileToUpload);
      objectUrlRef.current = newPreviewUrl;

      setFile(fileToUpload);
      setPreview(newPreviewUrl);
      if (step === 'pick') setStep('form');
    } catch (err) {
      console.error(err);
      setError('Failed to process image');
    } finally {
      setIsCompressing(false);
    }
  };

  // Cleanup memory on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  const toggleFaceShape = (shape) => {
    if (shape === 'All Face Shapes') {
      setFaceShapes(['All Face Shapes']);
      return;
    }
    setFaceShapes(prev => {
      const withoutAll = prev.filter(s => s !== 'All Face Shapes');
      return withoutAll.includes(shape)
        ? withoutAll.filter(s => s !== shape)
        : [...withoutAll, shape];
    });
  };

  const handlePriceChange = (e) => {
    setPrice(e.target.value.replace(/[^0-9.]/g, ''));
  };

  const handleSubmit = async () => {
    if (!name.trim()) return setError('Style name is required');
    if (!priceValid) return setError('Please enter a valid price');
    if (!preview) return setError('Please upload a photo');

    setSaving(true);
    setError('');

    const toastId = toast.loading(isEditMode ? 'Saving changes...' : 'Publishing to Global Vault...');

    try {
      let imageUrl = editItem?.imageUrl || '';

      if (file) {
        // Delete old image if editing and replacing
        if (isEditMode && editItem?.imageUrl) {
          try {
            const oldRef = ref(storage, editItem.imageUrl);
            await deleteObject(oldRef);
          } catch (e) { /* ignore */ }
        }

        const storageRef = ref(storage, `global_gallery/${user.uid}/${Date.now()}_${file.name}`);
        await uploadBytes(storageRef, file);
        imageUrl = await getDownloadURL(storageRef);
      }

      const sharedFields = {
        name: name.trim().toUpperCase(),
        description: description.trim(),
        price: parsedPrice,
        category,
        duration,
        faceShapes,
        imageUrl,
        barberId: user.uid,
        barberName: safeDisplayName,
        businessName: safeBusinessName,
        barberPhoto: safePhoto,
        location: safeLocation,
        referralCode: safeReferral,
        updatedAt: serverTimestamp(),
      };

      if (isEditMode) {
        await updateDoc(doc(db, 'barbers', user.uid, 'portfolio', editItem.id), sharedFields);
        if (editItem.galleryId) {
          await updateDoc(doc(db, 'global_gallery', editItem.galleryId), sharedFields).catch(() => {});
        }
        toast.success('Style updated successfully!', { id: toastId });
      } else {
        const batch = writeBatch(db);
        const portfolioRef = doc(collection(db, 'barbers', user.uid, 'portfolio'));
        const galleryRef = doc(collection(db, 'global_gallery'));

        batch.set(portfolioRef, {
          ...sharedFields,
          galleryId: galleryRef.id,
          creatorId: user.uid,
          createdAt: serverTimestamp(),
          votesCount: 0,
          promotionStatus: 'approved',
        });

        batch.set(galleryRef, {
          ...sharedFields,
          portfolioId: portfolioRef.id,
          creatorName: safeDisplayName,
          recommendedStylistName: safeBusinessName || safeDisplayName,
          createdAt: serverTimestamp(),
          votesCount: 0,
          promotionStatus: 'approved',
          status: 'approved',
          isPromoted: false,
        });

        await batch.commit();
        toast.success('Style published to Global Vault!', { id: toastId });
      }

      onClose();
    } catch (err) {
      console.error('[BarberUpload]', err);
      setError('Failed to save style. Please try again.');
      toast.error('Upload failed', { id: toastId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={modalHeader}>
          <div>
            <p style={eyebrow}>ESDRAS STYLIST PRO</p>
            <h2 style={title}>{isEditMode ? 'EDIT STYLE' : 'SHARE YOUR WORK'}</h2>
          </div>
          <button onClick={onClose} style={closeBtn}>✕</button>
        </div>

        {step === 'pick' && (
          <label style={uploadZone}>
            <span style={{ fontSize: '3rem', marginBottom: '16px' }}>📸</span>
            <span style={{ color: GOLD, fontWeight: '900', letterSpacing: '1px' }}>SELECT A PHOTO</span>
            <span style={{ fontSize: '0.7rem', opacity: 0.6, marginTop: '8px', textAlign: 'center' }}>
              Clear, well-lit photo recommended
            </span>
            <input ref={fileRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
          </label>
        )}

        {step === 'form' && (
          <div style={formBody}>
            <div style={previewWrapper}>
              <img src={preview} alt="Style preview" style={previewImg} />
              <label style={changePhotoBtn}>
                ✎ CHANGE PHOTO
                <input ref={fileRef} type="file" hidden accept="image/*" onChange={handleFileSelect} />
              </label>
            </div>

            {/* Rest of the form remains the same as previous version */}
            <div style={fieldGroup}>
              <label style={fieldLabel}>STYLE NAME *</label>
              <input style={inputStyle} placeholder="e.g. LOW SKIN FADE WITH BEARD" value={name} onChange={(e) => setName(e.target.value)} maxLength={50} />
            </div>

            <div style={fieldGroup}>
              <label style={fieldLabel}>PRICE (₦) *</label>
              <div style={priceWrapper}>
                <span style={currencyPrefix}>₦</span>
                <input style={{ ...inputStyle, paddingLeft: '42px' }} type="text" inputMode="decimal" placeholder="3500" value={price} onChange={handlePriceChange} />
              </div>
            </div>

            <div style={twoCol}>
              <div style={fieldGroup}>
                <label style={fieldLabel}>CATEGORY</label>
                <select style={selectStyle} value={category} onChange={e => setCategory(e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={fieldLabel}>DURATION</label>
                <select style={selectStyle} value={duration} onChange={e => setDuration(e.target.value)}>
                  {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div style={fieldGroup}>
              <label style={fieldLabel}>IDEAL FOR FACE SHAPES</label>
              <div style={chipRow}>
                {FACE_SHAPES.map(shape => {
                  const active = faceShapes.includes(shape);
                  return (
                    <button key={shape} onClick={() => toggleFaceShape(shape)} style={{ ...chip, background: active ? GOLD : 'rgba(255,255,255,0.05)', color: active ? '#000' : 'rgba(255,255,255,0.7)' }}>
                      {shape}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={fieldGroup}>
              <label style={fieldLabel}>DESCRIPTION <span style={{ opacity: 0.5 }}>(optional)</span></label>
              <textarea style={textareaStyle} placeholder="Describe technique, aftercare..." value={description} onChange={(e) => setDescription(e.target.value)} maxLength={280} />
              <p style={charCountStyle}>{description.length}/280</p>
            </div>

            {error && <p style={errorStyle}>{error}</p>}

            <button onClick={handleSubmit} disabled={saving || isCompressing || !name.trim() || !priceValid || !preview} style={{ ...submitBtn, opacity: (saving || isCompressing) ? 0.6 : 1 }}>
              {saving ? 'PUBLISHING...' : isCompressing ? 'COMPRESSING...' : isEditMode ? '✓ SAVE CHANGES' : '⚡ PUBLISH TO GLOBAL VAULT'}
            </button>

            {isEditMode && <button onClick={onClose} style={cancelBtn}>CANCEL</button>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ==================== STYLES ==================== */
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.96)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', fontFamily: 'Montserrat, sans-serif' };

const modal = { background: DARK, border: `1px solid rgba(184,134,11,0.35)`, borderRadius: '28px', width: '100%', maxWidth: '460px', maxHeight: '92vh', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' };

const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '24px 24px 0' };
const eyebrow = { fontSize: '0.55rem', color: GOLD, letterSpacing: '3px', fontWeight: '900', margin: '0 0 4px' };
const title = { color: 'white', fontSize: '1.35rem', fontWeight: '900', margin: 0, letterSpacing: '1.5px' };
const closeBtn = { background: 'rgba(255,255,255,0.08)', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '50%', fontSize: '1.1rem', cursor: 'pointer' };

const uploadZone = { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 30px', margin: '20px 24px', border: `2px dashed rgba(184,134,11,0.5)`, borderRadius: '20px', cursor: 'pointer', background: 'rgba(184,134,11,0.03)' };

const formBody = { padding: '20px 24px 32px', display: 'flex', flexDirection: 'column', gap: '20px', overflowY: 'auto' };

const previewWrapper = { position: 'relative', borderRadius: '18px', overflow: 'hidden', border: `1px solid rgba(184,134,11,0.4)` };
const previewImg = { width: '100%', height: '210px', objectFit: 'cover' };
const changePhotoBtn = { position: 'absolute', bottom: '12px', right: '12px', background: 'rgba(0,0,0,0.8)', color: GOLD, border: `1px solid ${GOLD}`, padding: '6px 14px', borderRadius: '20px', fontSize: '0.62rem', fontWeight: '900', cursor: 'pointer' };

const fieldGroup = { display: 'flex', flexDirection: 'column', gap: '6px' };
const fieldLabel = { fontSize: '0.58rem', color: GOLD, fontWeight: '900', letterSpacing: '1.5px' };

const inputStyle = { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', padding: '13px 14px', borderRadius: '12px', fontSize: '0.9rem', fontFamily: 'Montserrat, sans-serif', outline: 'none' };

const priceWrapper = { position: 'relative' };
const currencyPrefix = { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: GOLD, fontWeight: '900', fontSize: '1rem' };

const selectStyle = { ...inputStyle, cursor: 'pointer' };
const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' };

const chipRow = { display: 'flex', flexWrap: 'wrap', gap: '8px' };
const chip = { padding: '7px 15px', borderRadius: '20px', cursor: 'pointer', fontSize: '0.65rem', fontFamily: 'Montserrat, sans-serif', transition: 'all 0.15s' };

const textareaStyle = { ...inputStyle, height: '80px', resize: 'vertical', paddingTop: '12px' };
const charCountStyle = { fontSize: '0.55rem', opacity: 0.4, textAlign: 'right', margin: '2px 0 0' };

const submitBtn = { background: GOLD, color: '#000', border: 'none', padding: '16px', borderRadius: '50px', fontWeight: '900', fontSize: '0.9rem', cursor: 'pointer', letterSpacing: '1px', marginTop: '8px' };
const cancelBtn = { background: 'none', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)', padding: '13px', borderRadius: '50px', cursor: 'pointer', fontSize: '0.82rem' };

const errorStyle = { color: '#ff6b6b', fontSize: '0.8rem', margin: '4px 0', textAlign: 'center' };
